import { useState, useContext, createContext, PropsWithChildren, useCallback, useRef, useEffect } from "react";
import type { Product as IProduct, Button as IButton, ICurrentStream } from "~api/stream/interfaces";
import { getCategoryProductsPaginated } from "~api/stream";
import { useStreamContext } from "~contexts/Stream";
import { authFetch } from "~helpers/authFetch";

// Interface para cache de produtos por categoria (todos os produtos carregados)
interface CategoryCache {
  products: IProduct[]; // Todos os produtos já carregados para a categoria (encontrados na VTEX)
  total: number; // Total real de produtos na categoria (da base/planilha)
  notFoundInVTEX: number; // Total de produtos não encontrados na VTEX para a categoria
  notFoundProductIds: string[]; // IDs de produtos não encontrados na VTEX
  loadedFromSheet: number; // Quantos produtos da planilha já foram processados (encontrados + não encontrados)
}

interface IProductsContext {
  buttons: IButton[];
  setButtons: (value: IButton[]) => void;
  buttonActive: number;
  setButtonActive: (value: number) => void;
  productList: IProduct[];
  setProductList: (value: IProduct[]) => void;
  droppedFile: any;
  setDroppedFile: (value: any) => void;
  addRandomButton: () => Promise<void>;
  deleteButton: () => Promise<boolean>;
  deleteProduct: (value: number) => void;
  addProduct: (value: IProduct) => void;
  productRemoved: IProduct | null;
  loadCategoryProducts: (
    categoryId: number,
    page?: number,
    forceRefresh?: boolean,
    updateUI?: boolean
  ) => Promise<void>;
  loadingProducts: boolean;
  clearCategoryCache: (categoryId: number) => void;
  clearAllCache: () => void;
  selectCategoryFromCache: (categoryId: number) => void;
  getCategoryProductsFromCache: (categoryId: number) => IProduct[];
  getAllProductsFromCache: () => IProduct[];
  getCategoryNotFoundProductIds: (categoryId: number) => string[];
  updateCategoryCacheProducts: (categoryId: number, products: IProduct[]) => void;
  addProductToCategoryCache: (categoryId: number, product: IProduct) => void;
  notFoundInVTEX: number;
  setNotFoundInVTEX: (value: number) => void;
  notFoundProductIds: string[];
  setNotFoundProductIds: (value: string[]) => void;
  loadedProductsFromSheet: number;
  getCurrentLoadingSessionId: () => string;
}

const ProductsContext = createContext<IProductsContext>({
  buttons: [],
  setButtons: () => "",
  buttonActive: 0,
  setButtonActive: () => "",
  productList: [],
  setProductList: () => "",
  droppedFile: "",
  setDroppedFile: () => "",
  addRandomButton: async () => {},
  deleteButton: async () => false,
  deleteProduct: () => "",
  addProduct: () => "",
  productRemoved: null,
  loadCategoryProducts: async () => {},
  loadingProducts: false,
  clearCategoryCache: () => {},
  clearAllCache: () => {},
  selectCategoryFromCache: () => {},
  getCategoryProductsFromCache: () => [],
  getAllProductsFromCache: () => [],
  getCategoryNotFoundProductIds: () => [],
  updateCategoryCacheProducts: () => {},
  addProductToCategoryCache: () => {},
  notFoundInVTEX: 0,
  setNotFoundInVTEX: () => {},
  notFoundProductIds: [],
  setNotFoundProductIds: () => {},
  loadedProductsFromSheet: 0,
  getCurrentLoadingSessionId: () => '',
});

export const ProductsContextProvider = ({ children }: PropsWithChildren) => {
  const [buttons, setButtons] = useState<IButton[]>([]);
  const [buttonActive, setButtonActive] = useState<number>(0);
  const [productList, setProductList] = useState<IProduct[]>([]);
  const [droppedFile, setDroppedFile] = useState("");
  const [productRemoved, setProductRemoved] = useState<IProduct | null>(null);
  const [loadingProducts, setLoadingProducts] = useState<boolean>(false);
  const [notFoundInVTEX, setNotFoundInVTEX] = useState<number>(0);
  const [notFoundProductIds, setNotFoundProductIds] = useState<string[]>([]);
  const [loadedProductsFromSheet, setLoadedProductsFromSheet] = useState<number>(0);
  const { currentStream } = useStreamContext();
  
  // Cache de produtos por brand/stream_name e categoria (key: `${brand}-${streamName}-${categoryId}` -> CategoryCache)
  const productsCache = useRef<Map<string, CategoryCache>>(new Map());
  // Categoria atualmente ativa na UI (para evitar sobrescrever lista ao trocar de aba)
  const uiActiveCategoryIdRef = useRef<number | null>(null);
  // AbortControllers para cancelar requisições em andamento quando mudar de brand
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  // Referência ao stream atual para verificar se mudou durante carregamento
  const currentStreamRef = useRef<ICurrentStream | null>(currentStream);
  // ID único da sessão de carregamento atual (muda quando troca de stream)
  const loadingSessionIdRef = useRef<string>(`${Date.now()}-${Math.random()}`);
  
  // Atualizar currentStreamRef e criar nova sessão quando currentStream mudar
  useEffect(() => {
    currentStreamRef.current = currentStream;
    // Criar novo ID de sessão quando stream mudar
    loadingSessionIdRef.current = `${Date.now()}-${Math.random()}`;
  }, [currentStream]);
  
  // Função auxiliar para criar chave de cache
  const getCacheKey = useCallback((categoryId: number, brand?: string, streamName?: string) => {
    const brandName = brand || currentStream?.brand || '';
    const stream = streamName || currentStream?.name || '';
    return `${brandName}-${stream}-${categoryId}`;
  }, [currentStream]);

  const recalculateLoadedProductsFromSheet = useCallback(() => {
    if (!currentStream) {
      setLoadedProductsFromSheet(0);
      return;
    }
    
    const prefix = `${currentStream.brand}-${currentStream.name}-`;
    const totalLoaded = Array.from(productsCache.current.entries())
      .filter(([key]) => key.startsWith(prefix))
      .reduce(
        (sum, [, cache]) =>
          sum +
          (typeof cache.loadedFromSheet === "number"
            ? cache.loadedFromSheet
            : cache.products?.length || 0),
        0
      );
    setLoadedProductsFromSheet(totalLoaded);
  }, [currentStream]);

  const addRandomButton = useCallback(async () => {
    if (!currentStream?.id) return;

    try {
      const newButtonPayload = {
        x: `${buttons.length}`,
        y: "0",
        products: [],
        label: `nova categoria ${buttons.length + 1}`,
        filters: { sizes: [""] },
        image: "",
      };

      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/stream/${currentStream.id}/categories`,
        method: "POST",
        data: [newButtonPayload],
      });

      const createdButtons = await response.json();
      const createdButton = Array.isArray(createdButtons) ? createdButtons[0] : null;
      if (!createdButton?.id_button) return;

      let newActiveIndex = 0;
      setButtons((prevButtons) => {
        newActiveIndex = prevButtons.length;
        return [
          ...prevButtons,
          {
            ...createdButton,
            products: createdButton.products || [],
            filters: createdButton.filters || { sizes: [""] },
          },
        ];
      });
      setButtonActive(newActiveIndex);
    } catch (error) {
      console.error("Error creating category:", error);
    }
  }, [buttons.length, currentStream?.id, setButtonActive, setButtons]);

  // Função para limpar cache de uma categoria (útil quando produtos são adicionados/removidos)
  const clearCategoryCache = useCallback(
    (categoryId: number) => {
      const cacheKey = getCacheKey(categoryId);
      productsCache.current.delete(cacheKey);
      recalculateLoadedProductsFromSheet();
    },
    [recalculateLoadedProductsFromSheet, getCacheKey]
  );

  const deleteButton = useCallback(async (): Promise<boolean> => {
    if (!currentStream?.id || !buttons.length) return false;

    const activeButton = buttons[buttonActive];
    const deletedButtonId = activeButton?.id_button;
    if (!deletedButtonId) return false;

    try {
      await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/stream/${currentStream.id}/category/${deletedButtonId}`,
        method: "DELETE",
      });

      const newButtons = [...buttons];
      newButtons.splice(buttonActive, 1);
      setButtonActive(0);
      setButtons(newButtons);
      clearCategoryCache(deletedButtonId);
      return true;
    } catch (error) {
      console.error("Error deleting category:", error);
      return false;
    }
  }, [buttonActive, buttons, clearCategoryCache, currentStream?.id, setButtonActive, setButtons]);

  const deleteProduct = useCallback((index: number) => {
    const updatedButtons = buttons.map((button, buttonIndex) => {
      if (buttonIndex === buttonActive) {
        const products = button.products.filter((_, productIndex) => {
          const isNotDeleted = index !== productIndex;
          if (!isNotDeleted) {
            setProductRemoved(button.products[productIndex]);
            // Limpar cache da categoria quando produto é deletado
            if (button.id_button) {
              clearCategoryCache(button.id_button);
            }
          }
          return isNotDeleted;
        });

        return {
          ...button,
          products,
        };
      }
      return button;
    });
    setButtons(updatedButtons);
    
    // Recarregar página atual após deletar (fora do map para evitar loop)
    const activeButton = buttons[buttonActive];
    if (activeButton?.id_button) {
      // Atualizar lista de produtos removendo o deletado
      setProductList((prev) => prev.filter((_, i) => i !== index));
    }
  }, [buttons, buttonActive, clearCategoryCache, setButtons, setProductList]);

  const addProduct = useCallback((product: IProduct) => {
    const newButtons = [...buttons];
    const newButton = {
      ...buttons[buttonActive],
      products: [
        ...buttons[buttonActive].products,
        {
          ...product,
          id_button: buttons[buttonActive].id_button,
        },
      ],
    };

    newButtons[buttonActive] = newButton;
    setButtons(newButtons);
    
    // Limpar cache da categoria quando produto é adicionado
    if (buttons[buttonActive].id_button) {
      clearCategoryCache(buttons[buttonActive].id_button);
    }
  }, [buttons, buttonActive, clearCategoryCache, setButtons]);

  // Função para cancelar todas as requisições em andamento
  const cancelAllRequests = useCallback(() => {
    abortControllersRef.current.forEach((controller) => {
      controller.abort();
    });
    abortControllersRef.current.clear();
    setLoadingProducts(false);
  }, []);

  const loadCategoryProducts = useCallback(
    async (
      categoryId: number,
      _page: number = 1,
      forceRefresh: boolean = false,
      updateUI: boolean = true
    ) => {
      if (!currentStream) return;
      
      // Capturar stream atual e sessionId no início da função
      const streamAtStart = currentStreamRef.current;
      if (!streamAtStart) return;
      const sessionIdAtStart = loadingSessionIdRef.current;

      const cacheKey = getCacheKey(categoryId, streamAtStart.brand, streamAtStart.name);

      if (updateUI) {
        // Marcar esta categoria como a ativa para UI
        uiActiveCategoryIdRef.current = categoryId;
      }

      const cached = productsCache.current.get(cacheKey);

      // Se já processamos todos os produtos desta categoria (encontrados + não encontrados)
      // e não estamos forçando refresh, usar exclusivamente o cache (sem chamar o backend novamente)
      if (
        !forceRefresh &&
        cached &&
        cached.total &&
        typeof cached.loadedFromSheet === "number" &&
        cached.loadedFromSheet >= cached.total
      ) {
        if (updateUI) {
          uiActiveCategoryIdRef.current = categoryId;
          setProductList(cached.products);
          setNotFoundInVTEX(cached.notFoundInVTEX || 0);
          setNotFoundProductIds(cached.notFoundProductIds || []);
          setLoadingProducts(false);
        }
        return;
      }

      // Criar AbortController para esta requisição
      const abortController = new AbortController();
      abortControllersRef.current.set(cacheKey, abortController);

      // Quando vamos carregar de fato (primeira vez ou forceRefresh), iniciamos loading apenas se for para UI
      if (updateUI) {
        setLoadingProducts(true);
        setProductList([]);
      }

      let allProducts: IProduct[] = cached?.products ? [...cached.products] : [];
      let allNotFoundIds: string[] = [];
      let processedCount: number = cached?.loadedFromSheet || 0;
      let page = 1;
      let hasMore = true;
      let totalFromResponse = 0;

      try {
        while (hasMore) {
          // Verificar se a requisição foi cancelada ou se o stream mudou
          if (abortController.signal.aborted) {
            throw new Error('Request cancelled');
          }
          
          // Verificar se o sessionId mudou (stream mudou) durante o carregamento
          if (loadingSessionIdRef.current !== sessionIdAtStart) {
            abortController.abort();
            throw new Error('Request cancelled - stream changed');
          }
          
          // Verificar se o stream mudou durante o carregamento (verificação adicional)
          const currentStreamNow = currentStreamRef.current;
          if (!currentStreamNow || 
              currentStreamNow.brand !== streamAtStart.brand || 
              currentStreamNow.name !== streamAtStart.name) {
            abortController.abort();
            throw new Error('Request cancelled - stream changed');
          }

          const response = await getCategoryProductsPaginated(
            streamAtStart.name,
            categoryId,
            page,
            50,
            abortController.signal
          );

          const newProducts = response.products || [];
          const pagination = response.pagination || {};
          const notFoundCount = pagination.notFoundInVTEX || 0;
          const notFoundIds = pagination.notFoundProductIds || [];
          const buttonQuantity = pagination.buttonQuantity;

          // Atualizar quantidade do botão se disponível
          if (buttonQuantity !== undefined) {
            setButtons((prevButtons) => {
              if (prevButtons.length === 0) return prevButtons;
              return prevButtons.map((btn) => {
                if (btn.id_button === categoryId) {
                  return {
                    ...btn,
                    quantity: buttonQuantity,
                  };
                }
                return btn;
              });
            });
          }

          totalFromResponse = pagination.total || totalFromResponse || newProducts.length;
          hasMore = pagination.hasMore || false;
          page += 1;

          allProducts = [...allProducts, ...newProducts];
          allNotFoundIds = [...allNotFoundIds, ...notFoundIds];

          const uniqueNotFoundIds = Array.from(new Set(allNotFoundIds));
          const processedThisPage = newProducts.length + notFoundCount;
          processedCount += processedThisPage;
          const loadedFromSheet = processedCount;

          // Atualizar cache incrementalmente à medida que as páginas são carregadas
          productsCache.current.set(cacheKey, {
            products: allProducts,
            total: totalFromResponse,
            notFoundInVTEX: uniqueNotFoundIds.length,
            notFoundProductIds: uniqueNotFoundIds,
            loadedFromSheet,
          });
          recalculateLoadedProductsFromSheet();

          // Atualizar UI com progresso parcial, se aplicável
          if (updateUI && uiActiveCategoryIdRef.current === categoryId) {
            setProductList(allProducts);
            setNotFoundInVTEX(uniqueNotFoundIds.length);
            setNotFoundProductIds(uniqueNotFoundIds);
          }

          // Se por algum motivo o backend não sinalizar hasMore, mas já temos todos os produtos,
          // evitar loop infinito
          if (!hasMore || (totalFromResponse && allProducts.length >= totalFromResponse)) {
            hasMore = false;
          }
        }
      } catch (error: any) {
        // Não mostrar erro se foi cancelado intencionalmente
        if (error?.message !== 'Request cancelled') {
          console.error("Error loading category products:", error);
        }
        if (updateUI) {
          if (uiActiveCategoryIdRef.current === categoryId && error?.message !== 'Request cancelled') {
            setProductList([]);
            setNotFoundInVTEX(0);
            setNotFoundProductIds([]);
          }
          setLoadingProducts(false);
        }
        // Não propagar erro se foi cancelado
        if (error?.message !== 'Request cancelled') {
          throw error;
        }
      } finally {
        // Remover AbortController do mapa
        abortControllersRef.current.delete(cacheKey);
        if (updateUI) {
          setLoadingProducts(false);
        }
      }
    },
    [currentStream, recalculateLoadedProductsFromSheet, setButtons, getCacheKey]
  );
  
  // Função para limpar todo o cache da brand/stream atual
  const clearAllCache = useCallback(() => {
    if (!currentStream) {
      productsCache.current.clear();
      setLoadedProductsFromSheet(0);
    } else {
      // Limpar apenas cache da brand/stream atual
      const prefix = `${currentStream.brand}-${currentStream.name}-`;
      const keysToDelete: string[] = [];
      productsCache.current.forEach((_, key) => {
        if (key.startsWith(prefix)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach((key) => productsCache.current.delete(key));
      // Recalcular produtos carregados apenas da brand/stream atual
      recalculateLoadedProductsFromSheet();
    }
  }, [currentStream, recalculateLoadedProductsFromSheet]);

  // Obter produtos completos de uma categoria diretamente do cache (sem filtros de UI)
  const getCategoryProductsFromCache = useCallback((categoryId: number) => {
    const cacheKey = getCacheKey(categoryId);
    const cached = productsCache.current.get(cacheKey);
    return cached?.products || [];
  }, [getCacheKey]);

  // Obter IDs de produtos não encontrados na VTEX para uma categoria
  const getCategoryNotFoundProductIds = useCallback((categoryId: number): string[] => {
    const cacheKey = getCacheKey(categoryId);
    const cached = productsCache.current.get(cacheKey);
    return cached?.notFoundProductIds || [];
  }, [getCacheKey]);

  // Obter todos os produtos de todas as categorias diretamente do cache (sem duplicar productId)
  // Apenas da brand/stream atual
  const getAllProductsFromCache = useCallback((): IProduct[] => {
    if (!currentStream) return [];
    
    const allProducts: IProduct[] = [];
    const seen = new Set<string | number>();
    const prefix = `${currentStream.brand}-${currentStream.name}-`;

    productsCache.current.forEach((cache, key) => {
      // Apenas produtos da brand/stream atual
      if (key.startsWith(prefix)) {
        (cache.products || []).forEach((product) => {
          const productKey = product.provider?.productId ?? product.id;
          if (productKey && !seen.has(productKey)) {
            seen.add(productKey);
            allProducts.push(product);
          }
        });
      }
    });

    return allProducts;
  }, [currentStream]);

  // Atualizar produtos de uma categoria específica no cache (sem chamar backend)
  const updateCategoryCacheProducts = useCallback(
    (categoryId: number, products: IProduct[]) => {
      const cacheKey = getCacheKey(categoryId);
      const cached = productsCache.current.get(cacheKey);

      productsCache.current.set(cacheKey, {
        products,
        total: cached?.total ?? products.length,
        notFoundInVTEX: cached?.notFoundInVTEX ?? 0,
        notFoundProductIds: cached?.notFoundProductIds ?? [],
        loadedFromSheet: cached?.loadedFromSheet ?? products.length,
      });

      recalculateLoadedProductsFromSheet();
    },
    [recalculateLoadedProductsFromSheet, getCacheKey]
  );

  // Adicionar produto ao cache e à lista atual sem recarregar tudo
  const addProductToCategoryCache = useCallback(
    (categoryId: number, product: IProduct) => {
      const cacheKey = getCacheKey(categoryId);
      const cached = productsCache.current.get(cacheKey);

      // Verificar se o produto já existe no cache
      const productId = String(product.provider?.productId || product.id);
      const existingProducts = cached?.products || [];
      const alreadyExists = existingProducts.some(
        (p) => String(p.provider?.productId || p.id) === productId
      );

      if (alreadyExists) {
        return; // Produto já existe, não adicionar novamente
      }

      // Adicionar produto ao início da lista
      const updatedProducts = [
        { ...product, id_button: categoryId },
        ...existingProducts,
      ];

      // Atualizar cache
      productsCache.current.set(cacheKey, {
        products: updatedProducts,
        total: (cached?.total || 0) + 1,
        notFoundInVTEX: cached?.notFoundInVTEX ?? 0,
        notFoundProductIds: cached?.notFoundProductIds ?? [],
        loadedFromSheet: (cached?.loadedFromSheet || 0) + 1,
      });

      // Atualizar lista atual se esta categoria estiver ativa
      if (uiActiveCategoryIdRef.current === categoryId) {
        setProductList(updatedProducts);
      }

      // Atualizar quantidade do botão
      setButtons((prevButtons) => {
        return prevButtons.map((btn) => {
          if (btn.id_button === categoryId) {
            return {
              ...btn,
              quantity: (btn.quantity || 0) + 1,
            };
          }
          return btn;
        });
      });

      recalculateLoadedProductsFromSheet();
    },
    [recalculateLoadedProductsFromSheet, getCacheKey, setProductList, setButtons]
  );

  // Selecionar categoria apenas a partir do cache (sem chamadas para backend/VTEX)
  const selectCategoryFromCache = useCallback(
    (categoryId: number) => {
      uiActiveCategoryIdRef.current = categoryId;
      const cacheKey = getCacheKey(categoryId);
      const cached = productsCache.current.get(cacheKey);

      if (cached) {
        setProductList(cached.products || []);
        setNotFoundInVTEX(cached.notFoundInVTEX || 0);
        setNotFoundProductIds(cached.notFoundProductIds || []);
      } else {
        setProductList([]);
        setNotFoundInVTEX(0);
        setNotFoundProductIds([]);
      }
    },
    [setProductList, setNotFoundInVTEX, setNotFoundProductIds, getCacheKey]
  );

  // Função para obter o ID da sessão de carregamento atual
  const getCurrentLoadingSessionId = useCallback(() => {
    return loadingSessionIdRef.current;
  }, []);

  // Cancelar todas as requisições em andamento quando mudar de brand/stream
  useEffect(() => {
    // Cancelar imediatamente quando stream mudar
    cancelAllRequests();
    
    return () => {
      // Cleanup: cancelar todas as requisições quando o componente desmontar
      cancelAllRequests();
    };
  }, [currentStream?.id, cancelAllRequests]);

  return (
    <ProductsContext.Provider
      value={{
        droppedFile,
        setDroppedFile,
        buttons,
        setButtons,
        buttonActive,
        setButtonActive,
        productList,
        setProductList,
        addRandomButton,
        deleteButton,
        deleteProduct,
        addProduct,
        productRemoved,
        loadCategoryProducts,
        loadingProducts,
        clearCategoryCache,
        clearAllCache,
        selectCategoryFromCache,
        getCategoryProductsFromCache,
        getAllProductsFromCache,
        getCategoryNotFoundProductIds,
        updateCategoryCacheProducts,
        addProductToCategoryCache,
        notFoundInVTEX,
        setNotFoundInVTEX,
        notFoundProductIds,
        setNotFoundProductIds,
        loadedProductsFromSheet,
        getCurrentLoadingSessionId,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export const useProductsContext = () => {
  const context = useContext(ProductsContext);
  return context;
};
